import get from 'lodash.get';

import {
  giftAidStatus,
  giftAidStatusPath,
  hmrcCharityReferencePath
} from './constants';

/** Whether a save takes the HMRC charity reference away or swaps it for another one. */
export const swiftaidUnlinkReason = {
  cleared: 'cleared',
  changed: 'changed'
} as const;

export type SwiftaidUnlinkReason =
  (typeof swiftaidUnlinkReason)[keyof typeof swiftaidUnlinkReason];

const readReference = (organization: unknown): string => {
  const value: unknown = get(organization, hmrcCharityReferencePath);
  return typeof value === 'string' ? value.trim() : '';
};

/**
 * The API clears an organisation's Swiftaid link when its HMRC charity reference is
 * changed or cleared, so the save has to be confirmed first. Returns why the link would
 * be lost, or null when the save leaves it alone: the saved record is not linked, or the
 * reference is unchanged.
 *
 * Compares the saved record, not the form, for the link state: the form's status is not
 * re-read after a save. Any difference in the trimmed reference counts as a change, even
 * one of case only, because warning needlessly is cheaper than losing a link silently.
 */
export const getSwiftaidUnlinkReason = (
  savedOrganization: unknown,
  nextOrganization: unknown
): SwiftaidUnlinkReason | null => {
  if (get(savedOrganization, giftAidStatusPath) !== giftAidStatus.linked) {
    return null;
  }

  const saved = readReference(savedOrganization);
  const next = readReference(nextOrganization);
  if (saved === next) {
    return null;
  }

  return next ? swiftaidUnlinkReason.changed : swiftaidUnlinkReason.cleared;
};
